import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatSnackBar } from '@angular/material/snack-bar';
// Import  TranslateService
import { TranslateService } from '@ngx-translate/core';
import { MceSettings } from 'app/core/mce-config/mce-editor-config.constant';
import { NotificationTemplateInterface } from 'app/notifications/models';
import { NotificationService } from 'app/notifications/services/notification.service';
import { Base64DecodePipe } from 'app/shared/pipes/base64-decode.pipe';
import { stringEncodeToB64 } from 'app/shared/utilities/utility-methods.utility';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cz-notifications-configuration-item-dialog',
  templateUrl: './notifications-configuration-item-dialog.component.html',
  styleUrls: ['./notifications-configuration-item-dialog.component.scss'],
})
export class NotificationsConfigurationItemDialogComponent implements OnInit, OnDestroy {
  public addNewItem: boolean = true;
  public saving: boolean = false;
  public hasError: boolean = false;
  public tinyApiKey: string = environment.tinyMceKey;
  public editorConfig: any = MceSettings.default;
  public notificationTemplate: NotificationTemplateInterface;
  public titleControl: FormControl;
  public textControl: FormControl;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  // Inject TranslateService and CookieService
  constructor(@Inject(MAT_DIALOG_DATA) public data: NotificationTemplateInterface | undefined,
              private _notificationService: NotificationService,
              private _translateService: TranslateService,
              private _dialogRef: MatDialogRef<NotificationsConfigurationItemDialogComponent>,
              private _snackBar: MatSnackBar) {
    this.editorConfig = { ...this.editorConfig, height: 200 };
    this.titleControl = new FormControl('', Validators.required);
    this.textControl = new FormControl('', Validators.required);
  }

  public ngOnInit():void {
    this.notificationTemplate = this.data;
    this.titleControl.patchValue(this.notificationTemplate.title);
    this.textControl.patchValue(this.getHtmlFromText(this.notificationTemplate.templateText));

    if(this.notificationTemplate.id) {
      this.addNewItem = false;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public saveItem(): void {
    if(this.saving) { return; }

    if(this.titleControl.valid && this.textControl.valid) {
      const noteToSave: NotificationTemplateInterface = {
        ...this.notificationTemplate,
        title: this.titleControl.value,
        templateText: stringEncodeToB64(this.textControl.value),
      };

      this.saving = true;
      // existing notification update path
      if(noteToSave.id) {
        this._notificationService.updateNotificationTemplate(noteToSave)
        .pipe(takeUntil(this.destroy$))
        .subscribe((updatedTemplate: NotificationTemplateInterface) => {
          if(!updatedTemplate) { return; }
          // Static text to Language Variable updated
          this.openSnackBar(updatedTemplate.title + this._translateService.instant('$message_saved'));
          this.notificationTemplate = updatedTemplate;
          this.saving = false;
          this._dialogRef.close();
        });
        return;
      }

      this._notificationService.createNotificationTemplate(noteToSave)
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedTemplate: NotificationTemplateInterface) => {
        if(!savedTemplate) { return; }
        // Static text to Language Variable updated
        this.openSnackBar(savedTemplate.title + this._translateService.instant('$message_created'));
        this.notificationTemplate = savedTemplate;
        this.saving = false;
        this._dialogRef.close();
      });
    }
    this.hasError = !this.textControl.valid;
    this.titleControl.markAsTouched();
    this.titleControl.updateValueAndValidity();

  }

  public deleteItem(): void {
    if(this.notificationTemplate.id) {
      this.saving = true;
      this._notificationService.deleteNotificationTemplate(this.notificationTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((deletedTemplate: NotificationTemplateInterface) => {
        if(!deletedTemplate) { return; }
        // Static text to Language Variable updated
        this.openSnackBar(deletedTemplate.title + this._translateService.instant('$message_deleted'));
        this.notificationTemplate = undefined;
        this.saving = false;
        this._dialogRef.close();
      });
    }
  }

  private openSnackBar(message: string): void {
    this._snackBar.open(message, '', {
      duration: 2000,
    });
  }

  private getHtmlFromText(text: string): string {
    return new Base64DecodePipe().transform(text);
  }
}
